import { Component,OnDestroy,OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { finalize } from 'rxjs/operators';
import { NgxSpinnerService } from 'ngx-spinner';
import { BasePageComponent } from 'src/app/components/core/base-page/base-page.component';
import { ErisTableDataSource } from 'src/app/models/components/eris-table';
import { GroupShare } from 'src/app/models/services/admin/groups-share/groupShare';
import { GroupShareBusiness } from 'src/app/models/services/admin/groups-share/groupShareBusiness';
import { GroupShareDialogCloseEnum } from 'src/app/models/services/admin/groups-share/GroupShareDialogCloseEnum.enum';
import { TargetPlatforms } from 'src/app/models/services/targetPlatforms.enum';
import { GroupShareService } from 'src/app/services/admin/group-share.service';
import { DialogService } from 'src/app/services/core/dialog.service';
import { GroupShareDialogService } from 'src/app/services/dialogs/group-share-dialog.service';
import { UsersService } from 'src/app/services/users.service';
import { ThemeOptions } from 'src/app/theme-options';

interface GroupShareTableRow extends GroupShare {
  businessesCount: number;
  elementsCount: number;
}

@Component({
  selector: 'groups-share-maintenance',
  templateUrl: './groups-share-maintenance.component.html',
  styleUrls: ['./groups-share-maintenance.component.sass']
})
export class GroupsShareMaintenanceComponent extends BasePageComponent implements OnInit, OnDestroy {

  heading = 'Groups Share maintenance';
  subheading = '';
  icon = 'pe-7s-users icon-gradient bg-midnight-bloom';
  loadFinished = false;
  locked = false;
  expandedGroupId: number | null = null;
  private readonly expandedBusinessKeys = new Set<string>();
  groupsDataSource: ErisTableDataSource = {
    data: [],
    pageSize: 10,
    enablePagination: true,
    tableDefinition: [
      {
        headerDescription: 'ID',
        enableSort: true,
        sortFieldName: 'id',
        sortDefaultFieldValue: '',
        headerClass: 'group-column-id'
      },
      {
        headerDescription: 'Group name',
        enableSort: true,
        sortFieldName: 'name',
        sortDefaultFieldValue: '',
        headerClass: 'group-column-name'
      },
      {
        headerDescription: 'Businesses',
        enableSort: true,
        sortFieldName: 'businessesCount',
        sortDefaultFieldValue: '0',
        headerClass: 'group-column-businesses text-center'
      },
      {
        headerDescription: 'Advertisers',
        enableSort: true,
        sortFieldName: 'elementsCount',
        sortDefaultFieldValue: '0',
        headerClass: 'group-column-advertisers text-center'
      },
      {
        headerDescription: 'Platform',
        enableSort: true,
        sortFieldName: 'targetPlatform',
        sortDefaultFieldValue: '',
        headerClass: 'group-column-platform'
      },
      {
        headerDescription: 'Actions',
        enableSort: false,
        sortFieldName: '',
        sortDefaultFieldValue: '',
        headerClass: 'group-column-actions text-center'
      }
    ]
  } as ErisTableDataSource;


  private readonly platformNames: { [id: number]: string; } = {
    [TargetPlatforms.Meta]: 'Facebook',
    [TargetPlatforms.TikTok]: 'TikTok',
    [TargetPlatforms.Amazon]: 'Amazon'
  };


  constructor(
    public groupShareDialogService: GroupShareDialogService,
    private groupShareService: GroupShareService,
    public usersService: UsersService,
    private dialogService: DialogService,
    private globals: ThemeOptions,
    private spinnerService: NgxSpinnerService,
    private router: Router
  ) {
    super(usersService.hasGlobalActionRight('Admin',null,null),router);
  }


  ngOnInit(): void {
    this.globals.adminMode = true;
    this.loadFinished = false;
    this.loadGroups();
  }

  ngOnDestroy(): void {
    this.globals.adminMode = false;
  }

  private loadGroups(): void {
    this.spinnerService.show();
    this.groupShareService.getGroups().pipe(
        finalize(
          () => {
            this.spinnerService.hide();
            this.loadFinished = true;
          }
        )
      )
      .subscribe({
        next: (groups: GroupShare[]) => {
          this.setGroups(groups);
        },
        error:
          error => { this.setGroups([]);
          this.dialogService.showMessage('Error', this.getErrorMessage(error,'Error loading Group Shares'));
        }
      }
    );
  }

  toggleGroup(groupId: number): void {
    const normalizedGroupId = Number(groupId);
    if (this.expandedGroupId === normalizedGroupId) {
      this.expandedGroupId = null;
      this.clearExpandedBusinesses(normalizedGroupId);
      return;
    }
    this.expandedGroupId = normalizedGroupId;
    this.expandedBusinessKeys.clear();
  }

  isGroupExpanded(groupId: number): boolean {
    return (this.expandedGroupId === Number(groupId));
  }

  toggleBusiness(groupId: number,businessId: string): void {
    const key = this.getBusinessKey(groupId,businessId);
    if (this.expandedBusinessKeys.has(key)) {
      this.expandedBusinessKeys.delete(key);
      return;
    }
    this.expandedBusinessKeys.add(key);
  }

  isBusinessExpanded(groupId: number,businessId: string): boolean {
    return this.expandedBusinessKeys.has(this.getBusinessKey(groupId,businessId));
  }

  private getBusinessKey(groupId: number,businessId: string): string {
    return `${Number(groupId)}::${String(businessId)}`;
  }

  private clearExpandedBusinesses(groupId: number): void {
    const prefix = `${Number(groupId)}::`;
    Array.from(this.expandedBusinessKeys).forEach(
      key => {
        if (key.startsWith(prefix)) {
          this.expandedBusinessKeys.delete(key);
        }
      }
    );
  }

  getPlatformName(platformId: number): string {
    return (this.platformNames[Number(platformId)] ?? 'Unknown');
  }

  getPlatformIcon(platformId: number): string {
    return ('assets/images/target-platforms/' + `${Number(platformId)}.png`);
  }

  private setGroups(groups: GroupShare[]): void {
    const normalizedGroups: GroupShareTableRow[] = (groups ?? []).map(group => this.normalizeGroup(group));
    this.groupsDataSource = { ...this.groupsDataSource, data: normalizedGroups };
    if (this.expandedGroupId !== null && !normalizedGroups.some(group => Number(group.id) === this.expandedGroupId)) {
      this.expandedGroupId = null;
      this.expandedBusinessKeys.clear();
    }
  }

  private normalizeGroup(group: GroupShare): GroupShareTableRow {
    const businesses = this.normalizeBusinesses(group.businesses);
    return {...group, businesses, businessesCount: businesses.length, elementsCount: this.calculateElementsCount(businesses)};
  }

  private normalizeBusinesses(businesses: GroupShareBusiness[] | null | undefined): GroupShareBusiness[] {
    return (businesses ?? []).map(
      business => ({
        ...business,
        id: String(business.id ?? ''),
        name: String(business.name ?? ''),
        items: (business.items ?? []).map(
          item => ({
            ...item,
            id: String(item.id ?? ''),
            name: String(item.name ?? ''),
            type:String(item.type ?? '')
          })
        )
      })
    );
  }

  private calculateElementsCount(businesses: GroupShareBusiness[]): number {
    return businesses.reduce((total,business) => total + (business.items?.length ?? 0),0);
  }

  private getCurrentGroups(): GroupShare[] {
    if (!Array.isArray(this.groupsDataSource.data)) {
      return [];
    }
    return this.groupsDataSource.data as GroupShare[];
  }


  createGroupShare(): void {
    this.groupShareDialogService.createGroup().then(
      result => {
        if (result.action !== GroupShareDialogCloseEnum.CreateGroupShare) {
          return;
        }
        this.spinnerService.show();
        this.groupShareService.createGroupShare(result.data).pipe(
        finalize(() => { this.spinnerService.hide(); })
        ).subscribe({
          next: (savedGroup: GroupShare) => {
            const currentGroups = this.getCurrentGroups();
            this.setGroups([savedGroup, ...currentGroups]);
                this.dialogService.showMessage('Groups Share', 'Group Share added successfully');
            },
            error:
              error => { this.dialogService.showMessage('Error', this.getErrorMessage(error,'Error creating Group Share'));
            }
          }
        );
      }
    );
  }

  editGroupShare(row: GroupShare): void {
    this.groupShareDialogService.editGroup(row).then(
      result => {
        if (result.action !== GroupShareDialogCloseEnum.EditGroupShare) {
          return;
        }
        const groupId = Number(result.id ?? row.id);
        if (Number.isNaN(groupId) || groupId <= 0) {
          this.dialogService.showMessage('Error','Invalid Group Share ID');
          return;
        }
        this.spinnerService.show();
        this.groupShareService.updateGroupShare(groupId,result.data).pipe(
          finalize(() => { this.spinnerService.hide(); }))
          .subscribe({
            next: () => {
              const currentGroups = this.getCurrentGroups();
              const updatedGroup: GroupShare = { ...result.data, id: groupId };
              this.setGroups(currentGroups.map(group => Number(group.id) === groupId ? updatedGroup : group));
              this.expandedGroupId = groupId;
              this.clearExpandedBusinesses(groupId);
              this.dialogService.showMessage('Group Share','Group Share edited successfully');
            },
            error: error => {
              this.dialogService.showMessage('Error',this.getErrorMessage(error,'Error editing Group Share'));
            }
          }
        );
      }
    );
  }

  deleteGroup(group: GroupShare): void {
    const groupId = Number(group.id);
    if (Number.isNaN(groupId) || groupId <= 0) {
      this.dialogService.showMessage('Error', 'Invalid Group Share ID');
      return;
    }
    this.spinnerService.show();
    this.groupShareService.deleteGroupShare(groupId).pipe(
      finalize(() => { this.spinnerService.hide(); }))
      .subscribe({
        next: () => {
          const currentGroups = this.getCurrentGroups();
          this.setGroups(currentGroups.filter(currentGroup => Number(currentGroup.id) !== groupId));
          if (this.expandedGroupId === groupId) {
            this.expandedGroupId = null;
          }
          this.clearExpandedBusinesses(groupId);
          this.dialogService.showMessage('Groups Share','Group Share deleted successfully');
        },error: error => {
          this.dialogService.showMessage('Error',this.getErrorMessage(error,'Error deleting Group Share'));
        }
      }
    );
  }

  private getErrorMessage(error: any,defaultMessage: string): string {
    if (typeof error?.error === 'string') {
      return error.error;
    }
    if (error?.error?.message) {
      return error.error.message;
    }
    if (error?.message) {
      return error.message;
    }
    return defaultMessage;
  }
}